"use client";

import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogOverlay, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import ReactMarkdown from 'react-markdown';
import {
  Bot,
  X,
  Copy,
  Check,
  ExternalLink
} from 'lucide-react';
import { useAIResponseModal } from '@/contexts/AIResponseModalContext';

export function AIResponseModal() {
  const { isModalOpen, currentResponse, closeModal } = useAIResponseModal();
  const [copied, setCopied] = React.useState(false);

  React.useEffect(() => {
    if (isModalOpen) {
      console.log('AIResponseModal is opening with response:', currentResponse);
    }
  }, [isModalOpen, currentResponse]);

  const handleCopy = async () => {
    if (currentResponse?.content) {
      try {
        const contentText = typeof currentResponse.content === 'string'
          ? currentResponse.content
          : String(currentResponse.content);
        await navigator.clipboard.writeText(contentText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy text:', err);
      }
    }
  };

  const renderContent = () => {
    if (!currentResponse) return null;

    switch (currentResponse.type) {
      case 'text':
        return (
          <div className="prose max-w-none">
            <ReactMarkdown
              components={{
                // Enhanced markdown components for better readability
                p: ({ children }) => <p className="mb-4 leading-relaxed text-dark">{children}</p>,
                h1: ({ children }) => <h1 className="text-lg uppercase font-bold mb-0 text-mocha pb-2">{children}</h1>,
                h2: ({ children }) => <h2 className="text-xl font-semibold mt-6 mb-3 text-dark">{children}</h2>,
                h3: ({ children }) => <h3 className="text-lg font-semibold mb-2 text-dark">{children}</h3>,
                h4: ({ children }) => <h4 className="text-base font-semibold mb-2 text-dark">{children}</h4>,
                ul: ({ children }) => <ul className="list-disc list-outside ml-2 mb-4 space-y-1 text-dark">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal list-outside ml-2 mb-4 space-y-1 text-dark">{children}</ol>,
                li: ({ children }) => <li className="text-dark">{children}</li>,
                strong: ({ children }) => <strong className="font-semibold text-dark">{children}</strong>,
                em: ({ children }) => <em className="italic text-dark">{children}</em>,
                code: ({ children }) => <code className="bg-medium/50 px-2 py-1 rounded text-sm font-mono text-dark">{children}</code>,
                pre: ({ children }) => (
                  <pre className="bg-gray-100 p-4 rounded-lg text-sm font-mono overflow-x-auto mb-4 border border-gray-200">
                    {children}
                  </pre>
                ),
                blockquote: ({ children }) => (
                  <blockquote className="border-l-4 border-primary pl-4 italic text-dark mb-4 bg-primary/10 py-2 rounded-r">
                    {children}
                  </blockquote>
                ),
                a: ({ children, href }) => (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline inline-flex items-center gap-1"
                  >
                    {children}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ),
                table: ({ children }) => (
                  <div className="overflow-x-auto mb-4">
                    <table className="min-w-full border border-gray-200 rounded-lg">
                      {children}
                    </table>
                  </div>
                ),
                th: ({ children }) => (
                  <th className="border border-gray-200 px-4 py-2 bg-gray-50 font-semibold text-left">
                    {children}
                  </th>
                ),
                td: ({ children }) => (
                  <td className="border border-gray-200 px-4 py-2">
                    {children}
                  </td>
                ),
              }}
            >
              {typeof currentResponse.content === 'string' ? currentResponse.content : String(currentResponse.content)}
            </ReactMarkdown>
          </div>
        );

      case 'card':
        const card = currentResponse.content as any;
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{card.title}</h2>
              {card.description && (
                <div className="prose prose-sm max-w-none text-gray-700">
                  <ReactMarkdown>{card.description}</ReactMarkdown>
                </div>
              )}
            </div>

            {card.fields && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Details</h3>
                <div className="space-y-3">
                  {card.fields.map((field: any, idx: number) => (
                    <div key={idx} className="flex justify-between items-start py-2 border-b border-gray-100 last:border-b-0">
                      <span className="font-medium text-gray-700">{field.name}:</span>
                      <span className="text-gray-900 text-right max-w-xs">{field.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {card.actions && (
              <div className="flex flex-wrap gap-3">
                {card.actions.map((action: any, idx: number) => (
                  <Button key={idx} variant={action.type === 'primary' ? 'default' : 'outline'}>
                    {action.label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        );

      case 'list':
        const list = currentResponse.content as any;
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg border border-green-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">{list.title}</h2>
            </div>

            <div className="space-y-4">
              {list.items.map((item: any, idx: number) => (
                <div key={idx} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-4">
                    <span className="text-2xl flex-shrink-0">{item.icon}</span>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-2">{item.label}</h4>
                      {item.description && (
                        <div className="prose prose-sm max-w-none text-gray-700">
                          <ReactMarkdown>{item.description}</ReactMarkdown>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return (
          <div className="prose prose-lg max-w-none">
            <ReactMarkdown
              components={{
                p: ({ children }) => <p className="mb-4 leading-relaxed text-dark">{children}</p>,
                h1: ({ children }) => <h1 className="text-2xl font-bold mb-4 text-dark">{children}</h1>,
                h2: ({ children }) => <h2 className="text-xl font-semibold mb-3 text-dark">{children}</h2>,
                h3: ({ children }) => <h3 className="text-lg font-semibold mb-2 text-dark">{children}</h3>,
                ul: ({ children }) => <ul className="list-disc list-inside mb-4 space-y-1 text-dark">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal list-inside mb-4 space-y-1 text-dark">{children}</ol>,
                li: ({ children }) => <li className="text-dark">{children}</li>,
                strong: ({ children }) => <strong className="font-semibold text-dark">{children}</strong>,
                em: ({ children }) => <em className="italic text-dark">{children}</em>,
                code: ({ children }) => <code className="bg-medium/50 px-2 py-1 rounded text-sm font-mono text-dark">{children}</code>,
                pre: ({ children }) => (
                  <pre className="bg-medium/50 p-4 rounded-lg text-sm font-mono overflow-x-auto mb-4 border border-gray-200">
                    {children}
                  </pre>
                ),
              }}
            >
              {String(currentResponse.content)}
            </ReactMarkdown>
          </div>
        );
    }
  };

  return (
    <Dialog open={isModalOpen} onOpenChange={closeModal}>
      <DialogContent className="p-0 max-w-4xl max-h-[90vh] overflow-hidden flex flex-col gap-0 bg-light sm:max-w-4xl z-[9999]">
        <DialogHeader className="flex-shrink-0 border-b border-medium p-6">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-3 text-xl">
              <Bot className="w-6 h-6 text-mocha" />
              AI Assistant Response
              {currentResponse?.type && (
                <Badge variant="subtle" className="text-xs">
                  {currentResponse.type}
                </Badge>
              )}
            </DialogTitle>
            <div className="flex items-center gap-2">
              {currentResponse?.content && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopy}
                  className="flex items-center gap-2"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy
                    </>
                  )}
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={closeModal}
                className="h-8 w-8 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6">
          {currentResponse ? (
            <div className="text-base space-y-6">
              {renderContent()}

              {currentResponse.suggestions && currentResponse.suggestions.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Suggested Follow-ups:</h3>
                  <div className="flex flex-wrap gap-2">
                    {currentResponse.suggestions.map((suggestion, idx) => (
                      <Badge key={idx} variant="outline" className="text-sm cursor-pointer hover:bg-blue-100">
                        {suggestion}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-12">
              <Bot className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg">No response to display</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
